// [codex 2026-05-23] Real composer queue controller shared by main and sidebar chat.
import { useCallback, useEffect, useRef, useState } from "react";
import { loadQueue, saveQueue, type QueuedMessage } from "../MessageQueue";
import {
  buildChatWorkStorageKey,
  shouldMigrateDraftWorkStorage,
} from "./workStorageScope";
import type { DurableQueueSnapshotItem } from "../durable-queue-snapshot";

interface UseMessageQueueArgs {
  isRunning: boolean;
  sendNow: (text: string, queued?: QueuedMessage) => void;
  storageKey?: string;
  conversationId?: string;
}

export const CHAT_QUEUE_SNAPSHOT_EVENT = "fintheon:chat-queue-snapshot";

interface ChatQueueSnapshotDetail {
  conversationId: string;
  items: DurableQueueSnapshotItem[];
}

const LEGACY_QUEUE_STORAGE_KEY = "fintheon:message-queue";
type QueueDrainMode = "idle" | "one" | "all";

function createQueueItem(
  text: string,
  mode: QueuedMessage["mode"] = "queued",
  payload?: Pick<
    QueuedMessage,
    "images" | "referenceContext" | "riskFlowContext"
  >,
): QueuedMessage {
  return {
    id: `queue-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    text,
    timestamp: Date.now(),
    mode,
    ...payload,
  };
}

function removeLegacyQueue(): void {
  try {
    sessionStorage.removeItem(LEGACY_QUEUE_STORAGE_KEY);
  } catch {}
}

export function useMessageQueue({
  isRunning,
  sendNow,
  storageKey = buildChatWorkStorageKey("message-queue", ["default"]),
  conversationId,
}: UseMessageQueueArgs) {
  const [queue, setQueue] = useState<QueuedMessage[]>(() =>
    loadQueue(storageKey),
  );
  const [loadedStorageKey, setLoadedStorageKey] = useState(storageKey);
  const drainModeRef = useRef<QueueDrainMode>("idle");
  const queueRef = useRef(queue);
  const storageKeyRef = useRef(storageKey);
  const waitingForCompletionRef = useRef(false);
  const sawRunningSinceDispatchRef = useRef(false);
  const isRunningRef = useRef(isRunning);

  useEffect(() => {
    isRunningRef.current = isRunning;
  }, [isRunning]);

  useEffect(() => {
    queueRef.current = queue;
    saveQueue(queue, storageKeyRef.current);
  }, [queue]);

  useEffect(() => {
    removeLegacyQueue();
    const previousKey = storageKeyRef.current;
    if (previousKey === storageKey) {
      setLoadedStorageKey(storageKey);
      return;
    }
    storageKeyRef.current = storageKey;
    drainModeRef.current = "idle";
    waitingForCompletionRef.current = false;
    sawRunningSinceDispatchRef.current = false;
    const saved = loadQueue(storageKey);
    setQueue((current) => {
      if (
        shouldMigrateDraftWorkStorage(previousKey, storageKey) &&
        saved.length === 0 &&
        current.length > 0
      ) {
        saveQueue(current, storageKey);
        try {
          sessionStorage.removeItem(previousKey);
        } catch {}
        return current;
      }
      return saved;
    });
    setLoadedStorageKey(storageKey);
  }, [storageKey]);

  const drainFirst = useCallback(() => {
    if (isRunning || waitingForCompletionRef.current) return;
    const steeringIndex = queueRef.current.findIndex(
      (item) => item.mode === "steering" && !item.serverOwned,
    );
    const nextIndex =
      steeringIndex >= 0
        ? steeringIndex
        : queueRef.current.findIndex((item) => !item.serverOwned);
    const next = queueRef.current[nextIndex];
    if (!next) {
      drainModeRef.current = "idle";
      return;
    }
    const drainMode = drainModeRef.current;
    waitingForCompletionRef.current = true;
    sawRunningSinceDispatchRef.current = false;
    if (drainMode === "one") {
      drainModeRef.current = "idle";
    }
    setQueue((prev) => prev.filter((_, index) => index !== nextIndex));
    requestAnimationFrame(() => sendNow(next.text, next));
  }, [isRunning, sendNow]);

  useEffect(() => {
    if (isRunning && waitingForCompletionRef.current) {
      sawRunningSinceDispatchRef.current = true;
      return;
    }
    if (
      !isRunning &&
      waitingForCompletionRef.current &&
      sawRunningSinceDispatchRef.current
    ) {
      waitingForCompletionRef.current = false;
      sawRunningSinceDispatchRef.current = false;
    }
    if (drainModeRef.current === "idle" || isRunning || queue.length === 0)
      return;
    drainFirst();
  }, [drainFirst, isRunning, queue.length]);

  const addQueue = useCallback(
    (
      text: string,
      payload?: Pick<
        QueuedMessage,
        "images" | "referenceContext" | "riskFlowContext"
      >,
    ) => {
      const trimmed = text.trim();
      if (!trimmed) return null;
      if (isRunning) {
        drainModeRef.current = "all";
      }
      const item = createQueueItem(trimmed, "queued", payload);
      setQueue((prev) => [...prev, item]);
      return item.id;
    },
    [isRunning],
  );

  const markServerOwned = useCallback((id: string) => {
    setQueue((prev) =>
      isRunningRef.current
        ? prev.map((item) =>
            item.id === id ? { ...item, serverOwned: true } : item,
          )
        : prev.filter((item) => item.id !== id),
    );
  }, []);

  useEffect(() => {
    if (!conversationId) return;
    const handleSnapshot = (event: Event) => {
      const detail = (event as CustomEvent<ChatQueueSnapshotDetail>).detail;
      if (!detail || detail.conversationId !== conversationId) return;
      const recovered = detail.items.map((item): QueuedMessage => ({
        id: item.id,
        text: item.message,
        timestamp: Date.parse(item.createdAt) || Date.now(),
        mode: item.mode,
        serverOwned: true,
        referenceContext: item.context?.referenceContext,
        riskFlowContext: item.context?.riskFlowContext,
        attachmentCount: item.attachmentCount,
      }));
      setQueue((current) => [
        ...current.filter((item) => !item.serverOwned),
        ...recovered,
      ]);
    };
    window.addEventListener(CHAT_QUEUE_SNAPSHOT_EVENT, handleSnapshot);
    return () =>
      window.removeEventListener(CHAT_QUEUE_SNAPSHOT_EVENT, handleSnapshot);
  }, [conversationId]);

  const addSteeringQueue = useCallback((text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    drainModeRef.current = "all";
    setQueue((prev) => [createQueueItem(trimmed, "steering"), ...prev]);
  }, []);

  const editQueue = useCallback((id: string, text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    setQueue((prev) =>
      prev.map((item) => (item.id === id ? { ...item, text: trimmed } : item)),
    );
  }, []);

  const removeQueue = useCallback((id: string) => {
    setQueue((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const steerQueue = useCallback((id: string) => {
    drainModeRef.current = "all";
    setQueue((prev) => {
      const target = prev.find((item) => item.id === id);
      if (!target) return prev;
      return [
        { ...target, mode: "steering" as const },
        ...prev
          .filter((item) => item.id !== id)
          .map((item) =>
            item.mode === "steering"
              ? { ...item, mode: "queued" as const }
              : item,
          ),
      ];
    });
  }, []);

  const queueMessage = useCallback((id: string) => {
    setQueue((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, mode: "queued" as const } : item,
      ),
    );
  }, []);

  const pullQueueToDraft = useCallback((id: string): string | null => {
    const target = queueRef.current.find((item) => item.id === id);
    if (!target) return null;
    setQueue((prev) => prev.filter((item) => item.id !== id));
    return target.text;
  }, []);

  const reorderQueue = useCallback((fromIdx: number, toIdx: number) => {
    setQueue((prev) => {
      if (fromIdx < 0 || toIdx < 0) return prev;
      if (fromIdx >= prev.length || toIdx >= prev.length) return prev;
      const next = [...prev];
      const [item] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, item);
      return next;
    });
  }, []);

  const sendOne = useCallback(() => {
    drainModeRef.current = "all";
    drainFirst();
  }, [drainFirst]);

  const clearQueue = useCallback(() => {
    drainModeRef.current = "idle";
    waitingForCompletionRef.current = false;
    sawRunningSinceDispatchRef.current = false;
    setQueue([]);
  }, []);

  const scopedQueue = loadedStorageKey === storageKey ? queue : [];

  return {
    queue: scopedQueue,
    addQueue,
    markServerOwned,
    addSteeringQueue,
    editQueue,
    removeQueue,
    steerQueue,
    queueMessage,
    pullQueueToDraft,
    reorderQueue,
    sendOne,
    clearQueue,
    isStorageLoaded: loadedStorageKey === storageKey,
  };
}
